/**
 * Product Dashboard Component
 *
 * This component displays a dashboard for managing vendor products.
 * It allows users to view existing products, add new products, and retrieve terminal names.
 *
 * Dependencies:
 * - React for UI rendering
 * - React Router for navigation
 * - Dependency Injection (DI) with Awilix for services
 *
 * @module ProductDashboard
 */

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { container } from "../../infrastructure/container"
import { User } from "../../infrastructure/models/user"
import { SessionService } from "../../infrastructure/services/session"
import { Logger } from "../../infrastructure/services/logger"
import { ApiService } from "../../infrastructure/services/api"
import AddProductForm from "../components/AddProductForm"
import { useSearch } from "../components/ProtectedLayout"
import EditButton from "../../presentation/components/button/EditButton"
import CreateButton from "../components/button/CreateButton"
import { GetVendorProductsResponse } from "models/response/GetVendorProductResponse"

/**
 * Product Management Dashboard Component
 *
 * Displays and manages vendor products with CRUD operations. Handles:
 * - User authentication and session validation
 * - Product data fetching from API
 * - Search filtering
 * - Product creation via modal form
 *
 * @component
 * @returns {JSX.Element} Product management interface
 */
const ProductDashboard: React.FC = () => {
  // Service dependencies
  const sessionService = container.resolve<SessionService>("sessionService")
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")

  // Search context from ProtectedLayout
  const { searchQuery } = useSearch()

  // State management
  const [user, setUser] = useState<User | null>(null) // Current authenticated user
  const [showForm, setShowForm] = useState(false) // Controls add product form visibility
  const [products, setProducts] = useState<GetVendorProductsResponse[]>([]) // Product list
  const [loading, setLoading] = useState(true) // Data loading state
  const [error, setError] = useState<string | null>(null) // Error message
  const [showSuccessPopup, setShowSuccessPopup] = useState(false) // Success popup visibility
  const navigate = useNavigate() // Navigation hook

  /**
   * Fetches user session and product data on component mount
   *
   * Flow:
   * 1. Checks user session validity
   * 2. Redirects to login if session invalid
   * 3. Fetches terminal details for franchise ID
   * 4. Loads vendor products from API
   */
  useEffect(() => {
    const fetchUserAndProducts = async () => {
      try {
        // Check authentication
        const storedUserDetails = sessionService.user
        if (!storedUserDetails) {
          logger.warn("No user session found. Redirecting to login.")
          navigate("/login")
          return
        }
        setUser(storedUserDetails)

        // Get terminals from session
        const terminals = sessionService.terminals
        if (!terminals || terminals.length === 0) {
          throw new Error("No terminals found for this user")
        }

        // Fetch franchise ID from terminal
        const terminalDetails = await apiService.getTerminalDetails(
          terminals[0].terminalId
        )
        const franchiseId = terminalDetails.franchiseId
        if (!franchiseId) {
          throw new Error("Franchise ID not found in terminal details")
        }

        // Load vendor products
        const products = await apiService.getVendorProducts()
        setProducts(products)
      } catch (error) {
        logger.error("Error fetching data:", error)
        setError("Failed to load products. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchUserAndProducts()
  }, [navigate, sessionService, apiService, logger])

  /** Toggles product creation form visibility */
  const handleAddClick = () => {
    setShowForm(true)
  }

  /** Closes product creation form */
  const handleFormClose = () => {
    setShowForm(false)
  }

  /**
   * Handles new product submission
   *
   * @param {any} newProduct - Product data from form
   *
   * Converts form data to VendorProduct format and updates product list
   */
  const handleFormSubmit = (newProduct: any) => {
    const formattedProduct: GetVendorProductsResponse = {
      terminal: [
        {
          terminalId: newProduct.terminal,
          name: newProduct.terminalName,
          address: newProduct.address,
          terminalType: newProduct.terminalType,
          status: newProduct.status,
        },
      ],
      name: newProduct.name,
      productId: newProduct.productId,
      vendorCode: newProduct.vendorCode,
      quantity: newProduct.quantity,
      measure: newProduct.measure,
      price: newProduct.price,
      isRecommended: newProduct.isRecommended,
      offerPrice: newProduct.offerPrice,
      imageUrl: newProduct.imageUrl,
      isVeg: newProduct.isVeg,
      marketPrice: newProduct.marketPrice,
      lockerSize: newProduct.lockerSize,
      combo: newProduct.combo || [],
    }

    // Add to local state (optimistic update)
    setProducts((prevProducts) => [...prevProducts, formattedProduct])

    // Close form and show success popup
    setShowForm(false)
    setShowSuccessPopup(true)

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setShowSuccessPopup(false)
    }, 3000)
  }

  /**
   * Filters products based on search query
   *
   * Searches across:
   * - Terminal names
   * - Product name
   * - Price
   * - Quantity
   * - Vendor code
   */
  const filteredProducts = products.filter((product) => {
    const terminalNames = product.terminal.map((t) => t.name).join(", ")
    const searchString = [
      terminalNames,
      product.name,
      product.price.toString(),
      product.quantity.toString(),
      product.vendorCode,
    ]
      .join(" ")
      .toLowerCase()
      .trim()

    return searchString.includes(searchQuery.toLowerCase().trim())
  })

  // Render loading states
  if (!user) return <div>Loading user details...</div>
  if (loading) return <div className="text-center p-10">Loading...</div>
  if (error) return <div className="text-red-500 p-4">{error}</div>

  return (
    <div className="flex justify-between items-center mb-8 px-1 gap-20">
      <div className="flex-1 p-1">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-2">
          <h2
            data-automation-id="productmanagement-list-of-product-text"
            id="productmanagement-list-of-product-text"
            className="text-2xl font-semibold text-gray-800"
          >
            List of Products
          </h2>

          {/* Conditional Create Button */}
          {(user.role === 0 || user.role === 1 || user.role === 2) && (
            <CreateButton
              data-automation-id="productmanagement-form-create-vendorcode-product-button"
              id="productmanagement-form-create-vendorcode-product-button"
              setShowForm={setShowForm}
              buttonText="+ Create VendorCode"
            />
          )}
        </div>

        {showForm && (
          <div className="fixed inset-0 z-40 bg-gray bg-opacity-30 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[600px] max-h-[90vh] overflow-y-auto">
              <AddProductForm
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
              />
            </div>
          </div>
        )}

        {/* Product Table */}
        {filteredProducts.length === 0 ? (
          <p
            data-automation-id="productmanagement-searchbar-response-message"
            id="productmanagement-searchbar-response-message"
            className="text-center text-gray-500 mt-4"
          >
            {products.length > 0
              ? "No products match your search."
              : "No products available."}
          </p>
        ) : (
          <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
            <div
              className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
              style={{ maxHeight: "calc(104vh - 240px)" }}
            >
              <table className="min-w-full bg-white">
                <thead className="bg-gray-300 sticky top-0 z-10">
                  <tr>
                    <th className="py-2 px-4 text-sm align-middle">
                      Terminal Name
                    </th>
                    <th className="py-2 px-4 text-sm align-middle">
                      Product Name
                    </th>
                    <th className="py-2 px-4 text-sm align-middle">
                      Price (in Rupees)
                    </th>
                    <th className="py-2 px-4 text-sm align-middle">Quantity</th>
                    <th className="py-2 px-4 text-sm align-middle">
                      Vendor Code
                    </th>
                    <th className="py-2 px-4 text-sm align-middle">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 pl-10 border-t align-middle">
                        {product.terminal.map((t) => t.name).join(", ")}
                      </td>
                      <td className="py-2 pl-8 border-t align-middle">
                        {product.name}
                      </td>
                      <td className="py-2 pl-12 border-t align-middle">
                        {product.price}
                      </td>
                      <td className="py-2 pl-8 border-t align-middle">
                        {`${product.quantity} ${product.measure}`}
                      </td>
                      <td className="py-2 px-4 border-t align-middle">
                        {product.vendorCode}
                      </td>
                      <td className="py-2 pl-7 border-t align-middle">
                        <EditButton
                          data-automation-id="productmanagement-form-edit-button"
                          id="productmanagement-form-edit-button"
                          disabled={true}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
          data-automation-id="product-added-success-popup"
          id="product-added-success-popup"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-96 transform transition-all">
            <div className="flex flex-col items-center">
              {/* Checkmark icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-green-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Success message */}
              <p className="text-gray-600 text-center mb-4">
                New Product added successfully
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductDashboard